import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents, Polygon, GeoJSON } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '@/services/api';
import geodatosService, { GeodatosFeatureCollection } from '@/services/geodatosService';
import GeointelLayer from './GeointelLayer';
import { fieldService, AgentLocation, FieldMarker, FieldChatMessage } from '@/services/fieldService';
import {
    Eye,
    AlertTriangle,
    MessageSquare,
    Send,
    Skull,
    Target,
    Square,
    Circle as CircleIcon,
    Trash2,
    Layers,
    Video,
    Mic,
    Radio,
    Shield,
    Flame,
    School,
    HeartPulse,
    Users
} from 'lucide-react';

// --- Leaflet Configuration ---
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const createTacticalIcon = (color: string, type: 'agent' | 'blanco' | 'neutral' | 'alert' = 'agent') => {
    const icons = {
        agent: '<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>',
        blanco: '<path d="M9 14c1.5 0 3-1 3-1s1.5 1 3 1c1 0 1-1 1-1v-1c0-1-1-2-4-2s-4 1-4 2v1c0 0 0 1 1 1zM12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8zm-3-9c.6 0 1-.4 1-1s-.4-1-1-1-1 .4-1 1 .4 1 1 1zm6 0c.6 0 1-.4 1-1s-.4-1-1-1-1 .4-1 1 .4 1 1 1z"/>',
        neutral: '<path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm0-2a8 8 0 110-16 8 8 0 010 16zm0-12a4 4 0 100 8 4 4 0 000-8zm0 2a2 2 0 110 4 2 2 0 010-4z"/>',
        alert: '<path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4m0 4h.01" />'
    };

    return L.divIcon({
        className: 'tactical-marker',
        html: `<div style="width:36px;height:36px;background:${color};border:2px solid white;border-radius:${type === 'agent' ? '50%' : '12px'};box-shadow:0 0 15px ${color}80;display:flex;align-items:center;justify-content:center;transform: rotate(${type === 'alert' ? '0deg' : '0deg'});">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    ${icons[type] || icons.agent}
                </svg>
               </div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
    });
};

// --- Interfaces ---

interface OperationLocation {
    id: number;
    code: string;
    description: string;
    status: string;
    type: string;
    latitude: number;
    longitude: number;
    radius?: number;
    opcLevel?: string;
    ownerAgency?: { code: string };
}

interface DeconflictionAlert {
    id: number;
    operationA: OperationLocation;
    operationB: OperationLocation;
    detectedAt: string;
    description: string;
}

interface ChatMessage {
    id: string;
    sender: string;
    text: string;
    time: string;
    isSystem?: boolean;
}

// --- Helpers ---

function mapFieldChat(m: FieldChatMessage): ChatMessage {
    return {
        id: m.id,
        sender: m.sender,
        text: m.message,
        time: new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isSystem: m.system,
    };
}

// --- Map Sub-components ---

function MapEvents({ onMapClick, onMouseMove, onBoundsChanged }: {
    onMapClick: (lat: number, lng: number) => void,
    onMouseMove: (lat: number, lng: number) => void,
    onBoundsChanged?: (bbox: string) => void
}) {
    const map = useMapEvents({
        click(e) {
            onMapClick(e.latlng.lat, e.latlng.lng);
        },
        mousemove(e) {
            onMouseMove(e.latlng.lat, e.latlng.lng);
        },
        moveend() {
            if (onBoundsChanged) {
                const bounds = map.getBounds();
                onBoundsChanged(`${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`);
            }
        },
        zoomend() {
            if (onBoundsChanged) {
                const bounds = map.getBounds();
                onBoundsChanged(`${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`);
            }
        }
    });

    useEffect(() => {
        if (onBoundsChanged) {
            const bounds = map.getBounds();
            onBoundsChanged(`${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`);
        }
    }, [map, onBoundsChanged]);

    return null;
}

// --- Main Component ---

export default function MapPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [operations, setOperations] = useState<OperationLocation[]>([]);
    const [alerts, setAlerts] = useState<DeconflictionAlert[]>([]);
    const [showOperations, setShowOperations] = useState(true);
    const [agents, setAgents] = useState<AgentLocation[]>([]);
    const [showAgents, setShowAgents] = useState(true);
    const [chatOpen, setChatOpen] = useState(false);
    const [mediaSidebarOpen, setMediaSidebarOpen] = useState(true);
    const [messages, setMessages] = useState<ChatMessage[]>([
        { id: '1', sender: 'System', text: t('map.logStarted'), time: '10:00', isSystem: true }
    ]);
    const [newMessage, setNewMessage] = useState('');
    const [placingMarker, setPlacingMarker] = useState<string | null>(null);
    const [drawingPolygon, setDrawingPolygon] = useState<boolean>(false);
    const [drawingCircle, setDrawingCircle] = useState<boolean>(false);
    const [circleCenter, setCircleCenter] = useState<[number, number] | null>(null);
    const [currentPoints, setCurrentPoints] = useState<[number, number][]>([]);
    const [localMarkers, setLocalMarkers] = useState<any[]>([]);
    const [remoteMarkers, setRemoteMarkers] = useState<FieldMarker[]>([]);
    const [polygons, setPolygons] = useState<any[]>([]);
    const [mousePos, setMousePos] = useState<[number, number] | null>(null);
    const [inventoryOpen, setInventoryOpen] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);
    const [currentBbox, setCurrentBbox] = useState<string | undefined>(undefined);

    // Geodatos Layers State
    const [showCarabineros, setShowCarabineros] = useState(false);
    const [carabinerosData, setCarabinerosData] = useState<GeodatosFeatureCollection | null>(null);
    const [loadingCarabineros, setLoadingCarabineros] = useState(false);

    const [showBomberos, setShowBomberos] = useState(false);
    const [bomberosData, setBomberosData] = useState<GeodatosFeatureCollection | null>(null);
    const [loadingBomberos, setLoadingBomberos] = useState(false);

    const [showEducacion, setShowEducacion] = useState(false);
    const [educacionEscolarData, setEducacionEscolarData] = useState<GeodatosFeatureCollection | null>(null);
    const [educacionParvulariaData, setEducacionParvulariaData] = useState<GeodatosFeatureCollection | null>(null);
    const [educacionSuperiorData, setEducacionSuperiorData] = useState<GeodatosFeatureCollection | null>(null);
    const [loadingEducacion, setLoadingEducacion] = useState(false);

    const [showSalud, setShowSalud] = useState(false);
    const [saludData, setSaludData] = useState<GeodatosFeatureCollection | null>(null);
    const [loadingSalud, setLoadingSalud] = useState(false);

    // Geointel: amenazas naturales (sismos/incendios/volcanes) desde el backend.
    const [showGeointel, setShowGeointel] = useState(false);

    // --- Geodatos Data Fetchers ---
    useEffect(() => {
        if (showCarabineros && currentBbox) {
            setLoadingCarabineros(true);
            geodatosService.getCarabineros(currentBbox).then(data => {
                setCarabinerosData(data);
                setLoadingCarabineros(false);
                toast.success(t('map.toastCarabinerosLoaded', { count: data.features.length }));
            }).catch(e => {
                console.error(e);
                setLoadingCarabineros(false);
                toast.error(t('map.toastCarabinerosError'));
            });
        }
    }, [showCarabineros, currentBbox]);

    useEffect(() => {
        if (showBomberos && currentBbox) {
            setLoadingBomberos(true);
            geodatosService.getBomberos(currentBbox).then(data => {
                setBomberosData(data);
                setLoadingBomberos(false);
                toast.success(t('map.toastBomberosLoaded', { count: data.features.length }));
            }).catch(e => {
                console.error(e);
                setLoadingBomberos(false);
                toast.error(t('map.toastBomberosError'));
            });
        }
    }, [showBomberos, currentBbox]);

    useEffect(() => {
        if (showEducacion && currentBbox) {
            setLoadingEducacion(true);
            // Fetch the 3 endpoints in parallel to display education
            Promise.all([
                geodatosService.getEducacionEscolar(currentBbox),
                geodatosService.getEducacionParvularia(currentBbox),
                geodatosService.getEducacionSuperior(currentBbox)
            ]).then(([escolar, parvularia, superior]) => {
                setEducacionEscolarData(escolar);
                setEducacionParvulariaData(parvularia);
                setEducacionSuperiorData(superior);
                setLoadingEducacion(false);
                toast.success(t('map.toastEducacionLoaded', { count: escolar.features.length + parvularia.features.length + superior.features.length }));
            }).catch(e => {
                console.error(e);
                setLoadingEducacion(false);
                toast.error(t('map.toastEducacionError'));
            });
        }
    }, [showEducacion, currentBbox]);

    useEffect(() => {
        if (showSalud && currentBbox) {
            setLoadingSalud(true);
            geodatosService.getSalud(currentBbox).then(data => {
                setSaludData(data);
                setLoadingSalud(false);
                toast.success(t('map.toastSaludLoaded', { count: data.features.length }));
            }).catch(e => {
                console.error(e);
                setLoadingSalud(false);
                toast.error(t('map.toastSaludError'));
            });
        }
    }, [showSalud, currentBbox]);

    const loadData = useCallback(async () => {
        try {
            // Cargamos cada recurso de forma independiente para que si uno falla los otros funcionen
            const fetchOps = api.get('/api/v1/operations').catch(() => ({ data: { content: [] } }));
            const fetchAlerts = api.get('/api/v1/deconfliction/alerts').catch(() => ({ data: [] }));

            const [opsRes, alertsRes] = await Promise.all([
                fetchOps,
                fetchAlerts
            ]);

            setOperations(opsRes.data.content || []);
            setAlerts(alertsRes.data || []);
        } catch (error) {
            console.error('Error loading data', error);
        }
    }, []);

    useEffect(() => {
        loadData();
        const interval = setInterval(loadData, 5000);
        return () => clearInterval(interval);
    }, [loadData]);

    // --- Live Field Agents ---
    useEffect(() => {
        let active = true;
        const loadAgents = async () => {
            try {
                const data = await fieldService.getAgents();
                if (active) setAgents(data || []);
            } catch (error) {
                console.error('Error loading field agents', error);
            }
        };
        loadAgents();
        const interval = setInterval(loadAgents, 10000);
        return () => {
            active = false;
            clearInterval(interval);
        };
    }, []);

    // --- Shared Chat (initial load) ---
    useEffect(() => {
        let active = true;
        fieldService.getChat()
            .then(data => {
                if (!active) return;
                const mapped = (data || []).map(mapFieldChat);
                setMessages(prev => {
                    const existing = new Set(prev.map(m => m.id));
                    const merged = [...prev];
                    for (const m of mapped) {
                        if (!existing.has(m.id)) merged.push(m);
                    }
                    return merged;
                });
            })
            .catch(err => console.error('Error loading field chat', err));
        return () => { active = false; };
    }, []);

    // --- Shared Markers (initial load) ---
    useEffect(() => {
        let active = true;
        fieldService.getMarkers()
            .then(data => {
                if (active) setRemoteMarkers(data || []);
            })
            .catch(err => console.error('Error loading field markers', err));
        return () => { active = false; };
    }, []);

    // --- Live SSE stream (chat / markers / positions) ---
    useEffect(() => {
        const close = fieldService.streamFieldEvents({
            onChat: (m) => {
                setMessages(prev => {
                    if (prev.some(x => x.id === m.id)) return prev;
                    return [...prev, mapFieldChat(m)];
                });
            },
            onMarker: (mk) => {
                setRemoteMarkers(prev => {
                    if (prev.some(x => x.uid === mk.uid)) return prev;
                    return [...prev, mk];
                });
            },
            onPosition: () => {
                fieldService.getAgents()
                    .then(data => setAgents(data || []))
                    .catch(err => console.error('Error refreshing agents (SSE)', err));
            },
        });
        return close;
    }, []);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSendMessage = async () => {
        const text = newMessage.trim();
        if (!text) return;

        setNewMessage('');
        try {
            // Persistir en el chat compartido; el mensaje canónico (con id real)
            // se incorpora aquí y se deduplica frente a lo que llegue por SSE.
            const saved = await fieldService.postChat(text);
            setMessages(prev => {
                if (prev.some(m => m.id === saved.id)) return prev;
                return [...prev, mapFieldChat(saved)];
            });
        } catch (err) {
            console.error('Error sending field chat', err);
            // Respaldo local si falla la persistencia
            setMessages(prev => [...prev, {
                id: Date.now().toString(),
                sender: 'CENTRAL',
                text,
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            }]);
            toast.error(t('map.toastSendError'));
        }
    };

    const finishPolygon = () => {
        if (currentPoints.length < 3) {
            setDrawingPolygon(false);
            setCurrentPoints([]);
            return;
        }

        const newPoly = {
            id: `AREA-${Date.now()}`,
            points: currentPoints,
            color: '#3b82f6'
        };

        setPolygons(prev => [...prev, newPoly]);
        setDrawingPolygon(false);
        setCurrentPoints([]);

        setMessages(prev => [...prev, {
            id: Date.now().toString(),
            sender: 'Tactical',
            text: t('map.msgPerimeterEstablished', { id: newPoly.id.split('-')[1] }),
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            isSystem: true
        }]);

        // Aquí se enviaría al backend en el futuro
    };

    const removeTacticalElement = (id: string, type: 'marker' | 'area') => {
        if (type === 'marker') {
            setLocalMarkers(prev => prev.filter(m => m.id !== id));
        } else {
            setPolygons(prev => prev.filter(p => p.id !== id));
        }
    };

    const handleMapClick = async (lat: number, lng: number) => {
        // Lógica de dibujo de círculo
        if (drawingCircle) {
            if (!circleCenter) {
                setCircleCenter([lat, lng]);
            } else {
                // El segundo clic define el radio
                const L_center = L.latLng(circleCenter[0], circleCenter[1]);
                const L_edge = L.latLng(lat, lng);
                const radius = L_center.distanceTo(L_edge);

                const newCircle = {
                    id: `CIRCLE-${Date.now()}`,
                    center: circleCenter,
                    radius,
                    color: '#eab308'
                };

                setPolygons(prev => [...prev, newCircle]);
                setDrawingCircle(false);
                setCircleCenter(null);

                setMessages(prev => [...prev, {
                    id: Date.now().toString(),
                    sender: 'Tactical',
                    text: t('map.msgCircleEstablished', { radius: Math.round(radius) }),
                    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    isSystem: true
                }]);
            }
            return;
        }

        // Lógica de dibujo de polígono
        if (drawingPolygon) {
            setCurrentPoints(prev => [...prev, [lat, lng]]);
            return;
        }

        // Usamos una referencia o el estado actual para evitar cierres obsoletos
        if (!placingMarker) return;

        const currentPlacing = placingMarker;

        const markerId = `POI-${Date.now()}`;
        const markerCallsign = `OBJ-${currentPlacing.toUpperCase()}`;

        // Colocación local del marcador
        setLocalMarkers(prev => [...prev, {
            id: markerId,
            lat,
            lng,
            type: currentPlacing,
            callsign: markerCallsign
        }]);

        setMessages(prev => [...prev, {
            id: Date.now().toString(),
            sender: 'Tactical',
            text: t('map.msgMarkerDeployed', { type: currentPlacing.toUpperCase() }),
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            isSystem: true
        }]);

        setPlacingMarker(null);

        // Persistir el marcador en el campo compartido (terreno + puesto de mando).
        try {
            const saved = await fieldService.postMarker({
                callsign: markerCallsign,
                lat,
                lon: lng,
                type: currentPlacing,
                remarks: `Desplegado desde puesto de mando`,
            });
            setRemoteMarkers(prev => {
                if (prev.some(x => x.uid === saved.uid)) return prev;
                return [...prev, saved];
            });
        } catch (err) {
            console.error('Error persisting field marker', err);
        }
    };

    const handleExportLog = () => {
        const tacticalData = {
            sessionID: `TAC-${Date.now()}`,
            timestamp: new Date().toISOString(),
            markers: localMarkers,
            areas: polygons,
            alerts: alerts.length
        };

        const blob = new Blob([JSON.stringify(tacticalData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `TACTICAL_REPORT_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast.success(t('map.toastLogExported'));
    };

    return (
        <div className="flex h-[calc(100vh-10rem)] gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Styles for Leaflet Attribution to match Tactical Theme and avoid clipping */}
            <style>
                {`
                    .leaflet-control-attribution {
                        background-color: rgba(0, 0, 0, 0.2) !important;
                        color: #6b7280 !important;
                        border-radius: 6px !important;
                        margin-right: 50px !important; /* Avoid rounded corners */
                        margin-bottom: 10px !important;
                        backdrop-filter: blur(2px);
                        border: none !important;
                        font-size: 10px !important;
                    }
                    .leaflet-control-attribution a {
                        color: #3b82f6 !important;
                        text-decoration: none !important;
                    }
                    /* Hide the flag if present or other clutter */
                    .leaflet-control-attribution svg {
                        display: none; 
                    }
                `}
            </style>

            {/* Tactical Sidebar Left: Chat & Intel */}
            <div className={`flex flex-col gap-6 transition-all duration-500 pb-28 ${chatOpen ? 'w-96' : 'w-20'}`}>
                {/* Chat Panel */}
                <div className="flex-1 bg-gray-900/40 backdrop-blur-3xl border border-white/5 rounded-[2.5rem] flex flex-col overflow-hidden shadow-2xl">
                    <div className="p-6 border-b border-white/5 flex items-center justify-between">
                        {chatOpen && <h3 className="text-sm font-black text-white uppercase tracking-widest flex items-center gap-2">
                            <MessageSquare className="w-4 h-4 text-blue-500" /> {t('map.tacticalComms')}
                        </h3>}
                        <button
                            onClick={() => setChatOpen(!chatOpen)}
                            className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all mx-auto"
                        >
                            <MessageSquare className="w-5 h-5" />
                        </button>
                    </div>

                    {chatOpen && (
                        <>
                            <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
                                {messages.map((msg) => (
                                    <div key={msg.id} className={`flex flex-col ${msg.sender === 'CENTRAL' ? 'items-end' : 'items-start'}`}>
                                        {!msg.isSystem && <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest mb-1">{msg.sender} • {msg.time}</span>}
                                        <div className={`max-w-[85%] px-4 py-2 text-xs font-medium ${msg.isSystem ? 'bg-white/5 text-gray-500 italic rounded-lg text-[10px] w-full text-center' :
                                            msg.sender === 'CENTRAL' ? 'bg-blue-600 text-white rounded-2xl rounded-tr-none' :
                                                'bg-white/5 text-gray-300 rounded-2xl rounded-tl-none border border-white/5'
                                            }`}>
                                            {msg.text}
                                        </div>
                                    </div>
                                ))}
                                <div ref={chatEndRef} />
                            </div>
                            <div className="p-6 bg-white/[0.02] border-t border-white/5">
                                <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }} className="flex gap-2">
                                    <input
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        placeholder={t('map.broadcastPlaceholder')}
                                        className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-2 text-xs text-white focus:outline-none focus:border-blue-500"
                                    />
                                    <button type="submit" className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white hover:scale-110 active:scale-95 transition-all">
                                        <Send className="w-4 h-4" />
                                    </button>
                                </form>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Main Map Area */}
            <div className="flex-1 relative bg-gray-900 border border-white/5 rounded-[3.5rem] overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.3)]">
                {/* Map Controls Floating */}
                <div className="absolute top-8 left-8 z-[1000] flex flex-col gap-4">
                    <div className="bg-black/40 backdrop-blur-xl p-2 rounded-2xl border border-white/10 flex flex-col gap-2">
                        <div className="relative group/toggle">
                            <button
                                onClick={() => setShowOperations(!showOperations)}
                                className={`p-3 rounded-xl transition-all ${showOperations ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-white'}`}
                            ><Eye className="w-5 h-5" /></button>
                            <div className="absolute left-full ml-3 top-0 px-3 py-2 bg-gray-900 border border-white/10 rounded-lg text-[10px] text-white font-bold whitespace-nowrap opacity-0 invisible group-hover/toggle:opacity-100 group-hover/toggle:visible transition-all z-[2000] shadow-2xl">
                                {t('map.toggleOperations')}
                            </div>
                        </div>
                        <div className="relative group/toggle">
                            <button
                                onClick={() => setShowAgents(!showAgents)}
                                className={`p-3 rounded-xl transition-all relative ${showAgents ? 'bg-green-600 text-white shadow-[0_0_15px_#16a34a]' : 'text-gray-500 hover:text-white'}`}
                            >
                                <Users className="w-5 h-5" />
                                {showAgents && agents.length > 0 && (
                                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-white text-green-700 text-[9px] font-black rounded-full flex items-center justify-center">{agents.length}</span>
                                )}
                            </button>
                            <div className="absolute left-full ml-3 top-0 px-3 py-2 bg-gray-900 border border-white/10 rounded-lg text-[10px] text-white font-bold whitespace-nowrap opacity-0 invisible group-hover/toggle:opacity-100 group-hover/toggle:visible transition-all z-[2000] shadow-2xl">
                                {t('map.toggleAgents')}
                            </div>
                        </div>
                    </div>

                    {/* Geodatos Toggles */}
                    <div className="bg-black/40 backdrop-blur-xl p-2 rounded-2xl border border-white/10 flex flex-col gap-2">
                        <div className="px-2 py-1 text-[8px] font-black text-gray-500 uppercase tracking-widest text-center border-b border-white/5 mb-1">{t('map.geodataTitle')}</div>
                        <div className="relative group/toggle">
                            <button
                                onClick={() => setShowCarabineros(!showCarabineros)}
                                className={`p-3 rounded-xl transition-all relative ${showCarabineros ? 'bg-emerald-600 text-white shadow-[0_0_15px_#10b981]' : 'text-gray-500 hover:text-white hover:bg-white/5'}`}
                            >
                                <Shield className="w-5 h-5" />
                                {loadingCarabineros && <div className="absolute top-1 right-1 w-2 h-2 bg-white rounded-full animate-ping" />}
                            </button>
                            <div className="absolute left-full ml-3 top-0 px-3 py-2 bg-gray-900 border border-white/10 rounded-lg text-[10px] text-white font-bold whitespace-nowrap opacity-0 invisible group-hover/toggle:opacity-100 group-hover/toggle:visible transition-all z-[2000] shadow-2xl">
                                {t('map.layerCarabineros')}
                            </div>
                        </div>

                        <div className="relative group/toggle">
                            <button
                                onClick={() => setShowBomberos(!showBomberos)}
                                className={`p-3 rounded-xl transition-all relative ${showBomberos ? 'bg-red-600 text-white shadow-[0_0_15px_#dc2626]' : 'text-gray-500 hover:text-white hover:bg-white/5'}`}
                            >
                                <Flame className="w-5 h-5" />
                                {loadingBomberos && <div className="absolute top-1 right-1 w-2 h-2 bg-white rounded-full animate-ping" />}
                            </button>
                            <div className="absolute left-full ml-3 top-0 px-3 py-2 bg-gray-900 border border-white/10 rounded-lg text-[10px] text-white font-bold whitespace-nowrap opacity-0 invisible group-hover/toggle:opacity-100 group-hover/toggle:visible transition-all z-[2000] shadow-2xl">
                                {t('map.layerBomberos')}
                            </div>
                        </div>

                        <div className="relative group/toggle">
                            <button
                                onClick={() => setShowEducacion(!showEducacion)}
                                className={`p-3 rounded-xl transition-all relative ${showEducacion ? 'bg-blue-600 text-white shadow-[0_0_15px_#2563eb]' : 'text-gray-500 hover:text-white hover:bg-white/5'}`}
                            >
                                <School className="w-5 h-5" />
                                {loadingEducacion && <div className="absolute top-1 right-1 w-2 h-2 bg-white rounded-full animate-ping" />}
                            </button>
                            <div className="absolute left-full ml-3 top-0 px-3 py-2 bg-gray-900 border border-white/10 rounded-lg text-[10px] text-white font-bold whitespace-nowrap opacity-0 invisible group-hover/toggle:opacity-100 group-hover/toggle:visible transition-all z-[2000] shadow-2xl">
                                {t('map.layerEducacion')}
                            </div>
                        </div>

                        <div className="relative group/toggle">
                            <button
                                onClick={() => setShowSalud(!showSalud)}
                                className={`p-3 rounded-xl transition-all relative ${showSalud ? 'bg-pink-600 text-white shadow-[0_0_15px_#db2777]' : 'text-gray-500 hover:text-white hover:bg-white/5'}`}
                            >
                                <HeartPulse className="w-5 h-5" />
                                {loadingSalud && <div className="absolute top-1 right-1 w-2 h-2 bg-white rounded-full animate-ping" />}
                            </button>
                            <div className="absolute left-full ml-3 top-0 px-3 py-2 bg-gray-900 border border-white/10 rounded-lg text-[10px] text-white font-bold whitespace-nowrap opacity-0 invisible group-hover/toggle:opacity-100 group-hover/toggle:visible transition-all z-[2000] shadow-2xl">
                                {t('map.layerSalud')}
                            </div>
                        </div>

                        <div className="relative group/toggle">
                            <button
                                onClick={() => setShowGeointel(!showGeointel)}
                                className={`p-3 rounded-xl transition-all relative ${showGeointel ? 'bg-orange-600 text-white shadow-[0_0_15px_#ea580c]' : 'text-gray-500 hover:text-white hover:bg-white/5'}`}
                            >
                                <AlertTriangle className="w-5 h-5" />
                            </button>
                            <div className="absolute left-full ml-3 top-0 px-3 py-2 bg-gray-900 border border-white/10 rounded-lg text-[10px] text-white font-bold whitespace-nowrap opacity-0 invisible group-hover/toggle:opacity-100 group-hover/toggle:visible transition-all z-[2000] shadow-2xl">
                                {t('map.layerGeointel')}
                            </div>
                        </div>
                    </div>

                    {/* Tactical Toolbox */}
                    <div className="bg-black/40 backdrop-blur-xl p-2 rounded-2xl border border-white/10 flex flex-col gap-2">
                        <div className="px-2 py-1 text-[8px] font-black text-gray-500 uppercase tracking-widest text-center border-b border-white/5 mb-1">{t('map.toolsTitle')}</div>
                        <button
                            onClick={() => setPlacingMarker(placingMarker === 'blanco' ? null : 'blanco')}
                            className={`p-3 rounded-xl transition-all ${placingMarker === 'blanco' ? 'bg-red-600 text-white animate-pulse' : 'text-red-500 hover:bg-red-500/10'}`}
                            title={t('map.markTarget')}
                        ><Skull className="w-5 h-5" /></button>
                        <button
                            onClick={() => setPlacingMarker(placingMarker === 'neutral' ? null : 'neutral')}
                            className={`p-3 rounded-xl transition-all ${placingMarker === 'neutral' ? 'bg-gray-600 text-white animate-pulse' : 'text-gray-400 hover:bg-white/10'}`}
                            title={t('map.markNeutral')}
                        ><Target className="w-5 h-5" /></button>
                        <button
                            onClick={() => setPlacingMarker(placingMarker === 'alert' ? null : 'alert')}
                            className={`p-3 rounded-xl transition-all ${placingMarker === 'alert' ? 'bg-yellow-600 text-white animate-pulse' : 'text-yellow-500 hover:bg-yellow-500/10'}`}
                            title={t('map.alertZone')}
                        ><AlertTriangle className="w-5 h-5" /></button>
                        <button
                            onClick={() => {
                                if (drawingPolygon) {
                                    finishPolygon();
                                } else {
                                    setDrawingPolygon(true);
                                    setDrawingCircle(false);
                                    setPlacingMarker(null);
                                }
                            }}
                            className={`p-3 rounded-xl transition-all ${drawingPolygon ? 'bg-blue-600 text-white animate-pulse' : 'text-blue-400 hover:bg-blue-500/10'}`}
                            title={drawingPolygon ? t('map.finishArea') : t('map.drawArea')}
                        ><Square className="w-5 h-5" /></button>
                        <button
                            onClick={() => {
                                if (drawingCircle) {
                                    setDrawingCircle(false);
                                    setCircleCenter(null);
                                } else {
                                    setDrawingCircle(true);
                                    setDrawingPolygon(false);
                                    setPlacingMarker(null);
                                }
                            }}
                            className={`p-3 rounded-xl transition-all ${drawingCircle ? 'bg-yellow-600 text-white animate-pulse' : 'text-yellow-400 hover:bg-yellow-500/10'}`}
                            title={t('map.drawCircle')}
                        ><CircleIcon className="w-5 h-5" /></button>
                    </div>
                </div>

                {/* Tactical Inventory Toggle */}
                <div className="absolute top-8 left-28 z-[1000]">
                    <button
                        onClick={() => setInventoryOpen(!inventoryOpen)}
                        className={`p-3 rounded-2xl border backdrop-blur-xl transition-all ${inventoryOpen ? 'bg-blue-600 border-blue-500 text-white shadow-lg' : 'bg-black/40 border-white/10 text-gray-400 hover:text-white'}`}
                        title={t('map.tacticalInventory')}
                    >
                        <Layers className="w-5 h-5" />
                    </button>

                    {inventoryOpen && (
                        <div className="absolute top-14 left-0 w-64 bg-gray-900/95 backdrop-blur-3xl border border-white/10 rounded-3xl shadow-2xl p-4 animate-in fade-in slide-in-from-top-2 duration-300">
                            <div className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] mb-4 border-b border-white/5 pb-2">{t('map.tacticalInventory')}</div>

                            <div className="space-y-4 max-h-80 overflow-y-auto custom-scrollbar">
                                {/* Seccion Marcadores */}
                                <div>
                                    <div className="text-[9px] font-bold text-blue-400 uppercase mb-2">{t('map.markersCount', { count: localMarkers.length })}</div>
                                    <div className="space-y-1">
                                        {localMarkers.map(m => (
                                            <div key={m.id} className="flex items-center justify-between p-2 bg-white/5 rounded-lg border border-white/5 group/inv">
                                                <span className="text-[10px] text-gray-300 font-bold">{m.callsign}</span>
                                                <button onClick={() => removeTacticalElement(m.id, 'marker')} className="text-gray-600 hover:text-red-500 transition-colors">
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Seccion Areas */}
                                <div>
                                    <div className="text-[9px] font-bold text-yellow-400 uppercase mb-2">{t('map.areasCount', { count: polygons.length })}</div>
                                    <div className="space-y-1">
                                        {polygons.map(p => (
                                            <div key={p.id} className="flex items-center justify-between p-2 bg-white/5 rounded-lg border border-white/5">
                                                <span className="text-[10px] text-gray-300 font-bold">{p.id.split('-')[0]}-{p.id.slice(-4)}</span>
                                                <button onClick={() => removeTacticalElement(p.id, 'area')} className="text-gray-600 hover:text-red-500 transition-colors">
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {localMarkers.length === 0 && polygons.length === 0 && (
                                    <div className="text-[10px] text-gray-600 text-center py-4 italic">{t('map.noTacticalElements')}</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Status Indicator & Tools */}
                <div className="absolute top-8 right-8 z-[1000] flex gap-4">
                    {/* Quick Access: Centro en Vivo */}
                    <button
                        onClick={() => navigate('/live')}
                        className="bg-black/60 backdrop-blur-xl px-4 py-2 rounded-2xl border border-emerald-500/20 flex items-center gap-3 shadow-2xl transition-all duration-300 hover:border-emerald-500/40 hover:bg-emerald-900/20 group"
                        title={t('map.goToLiveCenter')}
                    >
                        <div className="flex flex-col items-end">
                            <span className="text-[8px] font-black text-emerald-500 uppercase tracking-widest leading-none mb-1">{t('map.liveCenter')}</span>
                            <span className="text-[10px] font-black text-white uppercase">{t('map.videoRadioPtt')}</span>
                        </div>
                        <div className="h-8 w-8 bg-emerald-600 group-hover:bg-emerald-500 rounded-xl flex items-center justify-center text-white transition-all shadow-lg shadow-emerald-600/20">
                            <Radio className="w-4 h-4" />
                        </div>
                    </button>

                    {/* Panel lateral Toggle */}
                    <button
                        onClick={() => setMediaSidebarOpen(!mediaSidebarOpen)}
                        className={`w-12 h-12 rounded-2xl border backdrop-blur-xl flex items-center justify-center transition-all ${mediaSidebarOpen ? 'bg-emerald-600 border-emerald-500 text-white shadow-lg' : 'bg-black/60 border-white/10 text-gray-400 hover:text-white'}`}
                        title={t('map.sidePanel')}
                    >
                        <Radio className="w-5 h-5" />
                    </button>
                </div>

                {/* Instruction Overlay */}
                {placingMarker && (
                    <div className="absolute inset-x-0 top-24 z-[1000] flex justify-center pointer-events-none">
                        <div className="bg-blue-600 text-white px-8 py-3 rounded-full text-xs font-black uppercase tracking-[0.2em] shadow-2xl border-2 border-white/20 animate-bounce">
                            {t('map.clickToPlace', { type: placingMarker })}
                        </div>
                    </div>
                )}

                {drawingPolygon && (
                    <div className="absolute inset-x-0 top-24 z-[1000] flex justify-center pointer-events-none">
                        <div className="bg-cyan-600 text-white px-8 py-3 rounded-full text-xs font-black uppercase tracking-[0.2em] shadow-2xl border-2 border-white/20 animate-pulse">
                            {currentPoints.length === 0 ? t('map.clickToStartArea') : t('map.addingPoints', { count: currentPoints.length })}
                        </div>
                    </div>
                )}

                {drawingCircle && (
                    <div className="absolute inset-x-0 top-24 z-[1000] flex justify-center pointer-events-none">
                        <div className="bg-yellow-600 text-white px-8 py-3 rounded-full text-xs font-black uppercase tracking-[0.2em] shadow-2xl border-2 border-white/20 animate-pulse">
                            {!circleCenter ? t('map.clickToPlaceCenter') : t('map.clickToDefineRadius')}
                        </div>
                    </div>
                )}

                <MapContainer center={[-33.4489, -70.6693]} zoom={13} style={{ height: '100%', width: '100%', cursor: (placingMarker || drawingPolygon || drawingCircle) ? 'crosshair' : 'grab', backgroundColor: '#1a1a1a' }} zoomControl={false}>
                    <TileLayer
                        url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                        keepBuffer={20}
                        updateWhenIdle={false}
                        updateWhenZooming={false}
                    />
                    <MapEvents
                        onMapClick={handleMapClick}
                        onMouseMove={(lat, lng) => setMousePos([lat, lng])}
                        onBoundsChanged={setCurrentBbox}
                    />

                    {/* --- GEOINTEL: amenazas naturales --- */}
                    {showGeointel && <GeointelLayer />}

                    {/* --- GEODATOS LAYERS --- */}
                    {showCarabineros && carabinerosData && (
                        <GeoJSON
                            key={`layer-carabineros-${Date.now()}`}
                            data={carabinerosData}
                            pointToLayer={(_feature, latlng) => {
                                return L.circleMarker(latlng, {
                                    radius: 6,
                                    fillColor: "#10b981",
                                    color: "#047857",
                                    weight: 2,
                                    opacity: 1,
                                    fillOpacity: 0.7
                                });
                            }}
                            onEachFeature={(feature, layer) => {
                                if (feature.properties) {
                                    const { nombre_unidad, comuna, tipo_unidad, calle, numero } = feature.properties;
                                    const content = `<div class="font-sans text-xs min-w-[150px]">
                                        <b class="text-emerald-500">🛡️ CARABINEROS</b><br/>
                                        <b>${nombre_unidad || tipo_unidad}</b><br/>
                                        ${calle || ''} ${numero || ''}<br/>
                                        <i>${comuna || ''}</i>
                                    </div>`;
                                    layer.bindPopup(content);
                                    layer.bindTooltip(`<b>${nombre_unidad || tipo_unidad}</b>`, { direction: 'top', className: 'tactical-tooltip' });
                                }
                            }}
                        />
                    )}

                    {showBomberos && bomberosData && (
                        <GeoJSON
                            key={`layer-bomberos-${Date.now()}`}
                            data={bomberosData}
                            pointToLayer={(_feature, latlng) => {
                                return L.circleMarker(latlng, {
                                    radius: 6,
                                    fillColor: "#dc2626",
                                    color: "#991b1b",
                                    weight: 2,
                                    opacity: 1,
                                    fillOpacity: 0.7
                                });
                            }}
                            onEachFeature={(feature, layer) => {
                                if (feature.properties) {
                                    const { nombre_compania, comuna, direccion, telefono } = feature.properties;
                                    const content = `<div class="font-sans text-xs min-w-[150px]">
                                        <b class="text-red-500">🚒 BOMBEROS</b><br/>
                                        <b>${nombre_compania}</b><br/>
                                        ${direccion || ''}<br/>
                                        <i>${comuna || ''}</i><br/>
                                        ${telefono ? '📞 ' + telefono : ''}
                                    </div>`;
                                    layer.bindPopup(content);
                                    layer.bindTooltip(`<b>${nombre_compania}</b>`, { direction: 'top', className: 'tactical-tooltip' });
                                }
                            }}
                        />
                    )}

                    {showEducacion && educacionEscolarData && (
                        <GeoJSON
                            key={`layer-escolar-${Date.now()}`}
                            data={educacionEscolarData}
                            pointToLayer={(_feature, latlng) => {
                                return L.circleMarker(latlng, { radius: 4, fillColor: "#2563eb", color: "#1e40af", weight: 1.5, fillOpacity: 0.6 });
                            }}
                            onEachFeature={(feature, layer) => {
                                if (feature.properties) {
                                    const { nombre, comuna, direccion, matricula_total } = feature.properties;
                                    const content = `<div class="font-sans text-xs min-w-[150px]"><b class="text-blue-500">🏫 ED. ESCOLAR</b><br/><b>${nombre}</b><br/>${direccion || ''} (${comuna})<br/>Pupilos: ${matricula_total || 0}</div>`;
                                    layer.bindPopup(content);
                                    layer.bindTooltip(`<b>${nombre}</b>`, { direction: 'top', className: 'tactical-tooltip' });
                                }
                            }}
                        />
                    )}

                    {showEducacion && educacionParvulariaData && (
                        <GeoJSON
                            key={`layer-parvularia-${Date.now()}`}
                            data={educacionParvulariaData}
                            pointToLayer={(_feature, latlng) => {
                                return L.circleMarker(latlng, { radius: 4, fillColor: "#8b5cf6", color: "#5b21b6", weight: 1.5, fillOpacity: 0.6 });
                            }}
                            onEachFeature={(feature, layer) => {
                                if (feature.properties) {
                                    const { nombre, comuna, direccion, matricula_total } = feature.properties;
                                    const content = `<div class="font-sans text-xs min-w-[150px]"><b class="text-violet-500">🧸 ED. PÁRVULOS</b><br/><b>${nombre}</b><br/>${direccion || ''} (${comuna})<br/>Pupilos: ${matricula_total || 0}</div>`;
                                    layer.bindPopup(content);
                                    layer.bindTooltip(`<b>${nombre}</b>`, { direction: 'top', className: 'tactical-tooltip' });
                                }
                            }}
                        />
                    )}

                    {showEducacion && educacionSuperiorData && (
                        <GeoJSON
                            key={`layer-superior-${Date.now()}`}
                            data={educacionSuperiorData}
                            pointToLayer={(_feature, latlng) => {
                                return L.circleMarker(latlng, { radius: 5, fillColor: "#d97706", color: "#92400e", weight: 2, fillOpacity: 0.7 });
                            }}
                            onEachFeature={(feature, layer) => {
                                if (feature.properties) {
                                    const { nombre, comuna, sede, tipo_institucion } = feature.properties;
                                    const content = `<div class="font-sans text-xs min-w-[150px]"><b class="text-amber-500">🎓 ED. SUPERIOR</b><br/><b>${nombre}</b><br/>${tipo_institucion || ''}<br/>Sede: ${sede || ''} (${comuna})</div>`;
                                    layer.bindPopup(content);
                                    layer.bindTooltip(`<b>${nombre}</b>`, { direction: 'top', className: 'tactical-tooltip' });
                                }
                            }}
                        />
                    )}

                    {showSalud && saludData && (
                        <GeoJSON
                            key={`layer-salud-${Date.now()}`}
                            data={saludData}
                            pointToLayer={(_feature, latlng) => {
                                return L.circleMarker(latlng, { radius: 5, fillColor: "#db2777", color: "#9d174d", weight: 2, fillOpacity: 0.7 });
                            }}
                            onEachFeature={(feature, layer) => {
                                if (feature.properties) {
                                    const { EstablecimientoGlosa, ComunaGlosa, TipoEstablecimientoGlosa, NombreVia, Numero, NivelComplejidadEstabGlosa } = feature.properties;
                                    const content = `<div class="font-sans text-xs min-w-[150px]">
                                        <b class="text-pink-500">🏥 ${TipoEstablecimientoGlosa ? TipoEstablecimientoGlosa.toUpperCase() : 'SALUD'}</b><br/>
                                        <b>${EstablecimientoGlosa}</b><br/>
                                        ${NivelComplejidadEstabGlosa || ''}<br/>
                                        ${NombreVia || ''} ${Numero || ''}<br/>
                                        <i>${ComunaGlosa || ''}</i>
                                    </div>`;
                                    layer.bindPopup(content);
                                    layer.bindTooltip(`<b>${EstablecimientoGlosa}</b>`, { direction: 'top', className: 'tactical-tooltip' });
                                }
                            }}
                        />
                    )}

                    {/* Feedback visual durante Dibujo de Polígono */}
                    {drawingPolygon && (
                        <>
                            {currentPoints.map((p, i) => (
                                <Circle key={i} center={p} radius={3} pathOptions={{ color: 'white', fillColor: 'white', fillOpacity: 1 }} />
                            ))}
                            {currentPoints.length > 0 && mousePos && (
                                <Polygon
                                    positions={[...currentPoints, mousePos]}
                                    pathOptions={{ color: '#3b82f6', dashArray: '5, 10', fillOpacity: 0.1, weight: 1 }}
                                />
                            )}
                        </>
                    )}

                    {/* Feedback visual durante Dibujo de Círculo */}
                    {drawingCircle && circleCenter && mousePos && (
                        <Circle
                            center={circleCenter}
                            radius={L.latLng(circleCenter).distanceTo(L.latLng(mousePos))}
                            pathOptions={{ color: '#eab308', dashArray: '5, 10', fillOpacity: 0.1 }}
                        />
                    )}

                    {/* Círculo en curso (Estatico) */}
                    {drawingCircle && circleCenter && (
                        <Circle center={circleCenter} radius={5} pathOptions={{ color: 'white', fillColor: 'white', fillOpacity: 1 }} />
                    )}

                    {/* Polígonos y Círculos guardados - Con protección de datos */}
                    {showOperations && polygons.map((p) => {
                        if (p.radius && p.center) {
                            return (
                                <Circle key={`${p.id}-${placingMarker ? 'pm' : ''}${drawingPolygon ? 'dp' : ''}${drawingCircle ? 'dc' : ''}`} center={p.center} radius={p.radius} pathOptions={{ color: p.color, fillOpacity: 0.1, weight: 2 }} interactive={!(placingMarker || drawingPolygon || drawingCircle)}>
                                    <Popup>
                                        <div className="p-2 font-black text-[10px] uppercase">{t('map.operationRadius', { radius: Math.round(p.radius) })}</div>
                                    </Popup>
                                </Circle>
                            );
                        } else if (p.points && p.points.length > 2) {
                            return (
                                <Polygon key={`${p.id}-${placingMarker ? 'pm' : ''}${drawingPolygon ? 'dp' : ''}${drawingCircle ? 'dc' : ''}`} positions={p.points} pathOptions={{ color: p.color, fillOpacity: 0.1, weight: 2 }} interactive={!(placingMarker || drawingPolygon || drawingCircle)}>
                                    <Popup>
                                        <div className="p-2 font-black text-[10px] uppercase">{t('map.securityZone', { id: p.id })}</div>
                                    </Popup>
                                </Polygon>
                            );
                        }
                        return null;
                    })}

                    {showOperations && operations.filter(op => op.latitude && op.longitude).map((op) => {
                        const hasConflict = alerts.some(a => a.operationA?.id === op.id || a.operationB?.id === op.id);
                        return (
                            <React.Fragment key={op.id}>
                                <Circle
                                    key={`${op.id}-${placingMarker ? 'pm' : ''}${drawingPolygon ? 'dp' : ''}${drawingCircle ? 'dc' : ''}`}
                                    center={[op.latitude, op.longitude]}
                                    radius={op.radius || 400}
                                    pathOptions={{
                                        color: hasConflict ? '#ef4444' : (op.status === 'ACTIVA' ? '#22c55e' : '#3b82f6'),
                                        fillColor: hasConflict ? '#ef4444' : (op.status === 'ACTIVA' ? '#22c55e' : '#3b82f6'),
                                        fillOpacity: hasConflict ? 0.3 : 0.08,
                                        weight: hasConflict ? 4 : (op.opcLevel === 'NIVEL_III' ? 3 : 1),
                                        dashArray: op.opcLevel === 'NIVEL_III' ? '10, 10' : ''
                                    }}
                                    interactive={!(placingMarker || drawingPolygon || drawingCircle)}
                                />
                                <Marker
                                    position={[op.latitude, op.longitude]}
                                    icon={createTacticalIcon(
                                        hasConflict ? '#ef4444' : (op.status === 'ACTIVA' ? '#22c55e' : '#3b82f6'),
                                        'neutral'
                                    )}
                                >
                                    <Popup className="tactical-popup">
                                        <div className="p-3 bg-gray-900 text-white min-w-[200px]">
                                            <div className="font-black text-blue-400 text-lg mb-1">{op.code}</div>
                                            <div className="text-[10px] font-bold text-gray-500 mb-2 uppercase tracking-widest">{op.ownerAgency?.code || 'PDI'}</div>
                                            <p className="text-xs text-gray-300 mb-2">{op.description}</p>
                                            <div className={`text-center py-1 rounded text-[10px] font-black uppercase ${op.opcLevel === 'NIVEL_III' ? 'bg-red-500/20 text-red-500' : 'bg-green-500/20 text-green-500'}`}>
                                                {op.opcLevel?.replace('_', ' ')}
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            </React.Fragment>
                        );
                    })}

                    {/* Marcadores locales inmediatos */}
                    {showOperations && localMarkers.map((m) => (
                        <Marker
                            key={m.id}
                            position={[m.lat, m.lng]}
                            icon={createTacticalIcon(
                                m.type === 'blanco' ? '#ef4444' : m.type === 'alert' ? '#eab308' : '#3b82f6',
                                m.type as any
                            )}
                        >
                            <Popup>
                                <div className="p-2 font-black uppercase text-xs">{m.callsign}</div>
                            </Popup>
                        </Marker>
                    ))}

                    {/* Marcadores compartidos del campo (terreno + SSE) */}
                    {showOperations && remoteMarkers.filter(m => m.latitude && m.longitude).map((m) => (
                        <Marker
                            key={`field-marker-${m.uid}`}
                            position={[m.latitude, m.longitude]}
                            icon={createTacticalIcon(
                                m.type === 'blanco' ? '#ef4444' : m.type === 'alert' ? '#eab308' : '#3b82f6',
                                (m.type === 'blanco' || m.type === 'neutral' || m.type === 'alert') ? m.type : 'neutral'
                            )}
                        >
                            <Popup className="tactical-popup">
                                <div className="p-3 bg-gray-900 text-white min-w-[180px]">
                                    <div className="font-black text-blue-400 text-base mb-1">{m.callsign || t('map.markerDefault')}</div>
                                    {m.remarks && (
                                        <p className="text-xs text-gray-300 mb-1">{m.remarks}</p>
                                    )}
                                    <div className="text-[10px] text-gray-500">
                                        {new Date(m.createdAt).toLocaleString('es-CL')}
                                    </div>
                                </div>
                            </Popup>
                        </Marker>
                    ))}

                    {/* Live Field Agents */}
                    {showAgents && agents.filter(a => a.latitude && a.longitude).map((agent) => (
                        <Marker
                            key={`agent-${agent.userId}`}
                            position={[agent.latitude, agent.longitude]}
                            icon={createTacticalIcon('#16a34a', 'agent')}
                        >
                            <Popup className="tactical-popup">
                                <div className="p-3 bg-gray-900 text-white min-w-[180px]">
                                    <div className="font-black text-green-400 text-base mb-1">{agent.callsign}</div>
                                    {agent.team && (
                                        <div className="text-[10px] font-bold text-gray-500 mb-2 uppercase tracking-widest">{agent.team}</div>
                                    )}
                                    {typeof agent.batteryLevel === 'number' && (
                                        <div className="text-xs text-gray-300 mb-1">{t('map.battery', { level: agent.batteryLevel })}</div>
                                    )}
                                    <div className="text-[10px] text-gray-500">
                                        {t('map.lastSignal', { time: new Date(agent.lastUpdate).toLocaleString('es-CL') })}
                                    </div>
                                </div>
                            </Popup>
                        </Marker>
                    ))}
                </MapContainer>
            </div>

            {/* Tactical Notifications Bar Bottom */}
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-[1000] w-full max-w-4xl px-8">
                <div className="bg-gray-900/60 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-6 flex items-center justify-between shadow-2xl">
                    <div className="flex gap-10">
                        <div className="flex flex-col">
                            <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1">{t('map.conflictNodes')}</span>
                            <span className={`text-xl font-black ${alerts.length > 0 ? 'text-red-500' : 'text-white/40'}`}>{alerts.length}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1">{t('map.localMarkers')}</span>
                            <span className="text-xl font-black text-white">{localMarkers.length}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1">{t('map.airspace')}</span>
                            <span className="text-xl font-black text-green-500">{t('map.secure')}</span>
                        </div>
                    </div>
                    <div className="flex gap-4">
                        <div className="relative group/tooltip">
                            <button
                                onClick={handleExportLog}
                                className="px-6 py-2 bg-white/5 hover:bg-white/10 border border-white/5 rounded-2xl text-[10px] font-black uppercase tracking-widest text-gray-400 transition-all"
                            >
                                {t('map.export')}
                            </button>
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 px-4 py-2 bg-gray-900 border border-white/10 rounded-xl text-[10px] text-white font-bold whitespace-nowrap opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all z-[2000] shadow-2xl">
                                {t('map.exportTooltip')}
                            </div>
                        </div>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="px-6 py-2 bg-blue-600 rounded-2xl text-[10px] font-black uppercase tracking-widest text-white hover:scale-105 transition-all outline-none"
                        >
                            {t('map.missionControl')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Right Sidebar: Evidence & Quick Links */}
            <div className={`flex flex-col gap-6 transition-all duration-500 ${mediaSidebarOpen ? 'w-80' : 'w-0 opacity-0 overflow-hidden'}`}>

                {/* Centro en Vivo Quick Access Card */}
                <div
                    onClick={() => navigate('/live')}
                    className="bg-gray-900/40 backdrop-blur-3xl border border-emerald-500/10 rounded-[2.5rem] p-6 shadow-2xl cursor-pointer group hover:border-emerald-500/30 transition-all"
                >
                    <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center shadow-lg shadow-emerald-600/20 group-hover:scale-110 transition-transform">
                            <Radio className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h3 className="text-xs font-black text-white uppercase tracking-widest">{t('map.liveCenter')}</h3>
                            <p className="text-[9px] text-gray-500 font-medium">{t('map.videoStreamingRadioPtt')}</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                        <div className="flex items-center gap-2 px-3 py-2 bg-red-500/5 border border-red-500/10 rounded-xl">
                            <Video className="w-3.5 h-3.5 text-red-400" />
                            <span className="text-[9px] font-bold text-red-400 uppercase">{t('map.video')}</span>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-2 bg-indigo-500/5 border border-indigo-500/10 rounded-xl">
                            <Mic className="w-3.5 h-3.5 text-indigo-400" />
                            <span className="text-[9px] font-bold text-indigo-400 uppercase">{t('map.radioPtt')}</span>
                        </div>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-[9px] font-black text-emerald-500 uppercase tracking-widest group-hover:text-emerald-400 transition-colors">
                        <span>{t('map.openLiveCenter')}</span>
                        <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </div>
                </div>
            </div>
        </div >
    );
}
